import React, { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import ellipseImage from "../../../../assets/Ellipse 240.png";
import { ReactComponent as SendIcon } from "../../../../assets/send-26.svg";
import tail from "../../../../assets/tail.png";
import tail2 from "../../../../assets/tail2.png";
import { apiFetch } from "../../../../services/apiFetch";


const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

const AIConversation = ({ userId, subsection, onSystemThinkingChange, onUserTurnChange }) => {
  const [chatMessages, setChatMessages] = useState([]);
  const [summary, setSummary] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const chatBoxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setChatSessionId(uuidv4());
  }, []);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const fetchAIReply = async (message) => {
    if (!chatSessionId) return;
    setLoading(true);
    onSystemThinkingChange?.(true); // system thinking start

    try {
      const res = await apiFetch(
        `${apiBaseURL}/users/${userId}/agents/${subsection.subsection_url}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message, session_id: chatSessionId }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        setChatMessages((prev) => [...prev, ...data]);
      }
    } catch (err) {
      console.error("Chat error", err);
    } finally {
      setLoading(false);
      onSystemThinkingChange?.(false); // system thinking end
      onUserTurnChange?.(false); // user turn over as system reply finished
    }
  };

  useEffect(() => {
    if (!chatSessionId) return;
    const loadHistory = async () => {
      const res = await apiFetch(
        `${apiBaseURL}/users/${userId}/agents/${subsection.subsection_url}/history`
      );
      if (res.ok) {
        const data = await res.json();
        setSummary(data.summary || null);
        if (Array.isArray(data.messages) && data.messages.length > 0) {
          setChatMessages(data.messages);
        } else {
          setChatMessages([]);
          setLoading(true);
          try {
            const res2 = await apiFetch(
              `${apiBaseURL}/users/${userId}/agents/${subsection.subsection_url}`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  message: "What can you do for me?",
                  session_id: chatSessionId,
                }),
              }
            );
            if (res2.ok) {
              const reply = await res2.json();
              setChatMessages(reply);
            }
          } catch (err) {
            console.error("Starter chat error", err);
          } finally {
            setLoading(false);
          }
        }
      }
    };
    loadHistory();
  }, [userId, subsection.subsection_url, chatSessionId]);

  const sendMessage = async () => {
    if (!chatInput.trim() || !chatSessionId) return;
    onUserTurnChange?.(true); // user turn start

    const userMessage = {
      role: "user",
      content: chatInput,
      session_id: chatSessionId,
    };

    setChatMessages((prev) => [...prev, userMessage]);
    setChatInput("");
    await fetchAIReply(userMessage.content);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.style.height = "0px";
    const maxHeight = 132;
    const nextHeight = Math.min(inputRef.current.scrollHeight, maxHeight);
    inputRef.current.style.height = `${nextHeight}px`;
  }, [chatInput]);

  const renderMessage = (m, idx) => {
    const isUser = m.role === "user";
  
    return (
      <div
        key={idx}
        className={`mb-4 flex ${isUser ? "justify-end" : "justify-start"}`}  // ✅ REDUCED from mb-6 to mb-4
      >
        <div
          className={`min-w-[40px] max-w-[759px] px-[18px] py-[14px] rounded-[20px] relative`}  // ✅ REDUCED padding: px-21→18, py-18→14
          style={{
            background: isUser ? "#F5F5F5" : "#CBECEC",
            maxWidth: "80%",
            borderRadius: 20,
          }}
        >
          <p
            className="font-gotham whitespace-pre-wrap"
            style={{
              fontSize: isUser ? 14 : 15,  // ✅ REDUCED: user 15→14, system 16→15
              fontWeight: isUser ? 400 : 500,
              lineHeight: "135%",
              color: "#323232",
            }}
          >
            {m.content}
          </p>
  
          {/* SVG Tail placement */}
          {isUser ? (
            <img
              src={tail2}
              alt="Tail for User"
              style={{
                position: "absolute",
                bottom: "12%",
                right: -12, 
                width: "16px",
                height: "22px",
                userSelect: "none",
                pointerEvents: "none",
              }}
            />
          ) : (
            <img
              src={tail}
              alt="Tail for System"
              style={{
                position: "absolute",
                bottom: "12%",
                left: -12,
                width: "16px",
                height: "22px",
                userSelect: "none",
                pointerEvents: "none",
              }}
            />
          )}
        </div>
      </div>
    );
  };
  
  

  const historyMessages = chatMessages.filter((m) => String(m.session_id) !== String(chatSessionId));
  const ongoingMessages = chatMessages.filter((m) => String(m.session_id) === String(chatSessionId));

  return (
    <div className="w-full h-[805px] flex flex-col bg-white rounded-[20px] relative">
      <div
        ref={chatBoxRef}
        className="flex-1 overflow-y-auto px-6 py-4 scrollbar-hide"
        style={{ maxHeight: "calc(700px - 95px)" }}
      >
        {historyMessages.map(renderMessage)}
        {summary && historyMessages.length > 0 && (
          <div className="flex items-center my-6">
            <hr className="flex-grow border-gray-300" />
            <div className="px-4 py-2 bg-yellow-50 text-sm text-gray-700 rounded-lg border border-yellow-200 mx-4 font-gotham">
              <strong>Conversation so far:</strong> {summary}
            </div>
            <hr className="flex-grow border-gray-300" />
          </div>
        )}
        {ongoingMessages.map(renderMessage)}
        {loading && (
          <div className="flex justify-start mb-8">
            <div
              className="bubble-tail-left mr-6 min-w-[40px] max-w-[759px] px-[21px] py-[18px] rounded-[20px]"
              style={{
                background: "#CBECEC",
                maxWidth: "80%",
                borderRadius: 20,
                position: "relative",
              }}
            >
              <div className="flex gap-1">
                <span
                  className="w-2 h-2 bg-[#323232] rounded-full"
                  style={{
                    animation: "bounce 1.4s infinite ease-in-out both",
                    animationDelay: "0s",
                  }}
                />
                <span
                  className="w-2 h-2 bg-[#323232] rounded-full"
                  style={{
                    animation: "bounce 1.4s infinite ease-in-out both",
                    animationDelay: "0.16s",
                  }}
                />
                <span
                  className="w-2 h-2 bg-[#323232] rounded-full"
                  style={{
                    animation: "bounce 1.4s infinite ease-in-out both",
                    animationDelay: "0.32s",
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-auto h-auto pointer-events-none z-0"
      >
        <img
          src={ellipseImage}
          alt=""
          className="w-auto h-auto object-contain"
          style={{ maxHeight: "900px", maxWidth: "900px" }}
        />
      </div>

      <div className="relative px-6 mt-4 flex-shrink-0 z-10">
        <div
          className="w-full max-w-[844px] min-h-[55px] mx-auto flex items-start gap-3 bg-[#FFFFFF] rounded-[16px] px-4 py-2 shadow-sm"
          style={{ border: "3px solid #54B0AF" }}
        >
          <textarea
            ref={inputRef}
            rows={1}
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={handleKeyPress}
            className="flex-1 outline-none pt-1.5 font-gotham bg-transparent resize-none overflow-y-auto"
            placeholder="Ask me anything..."
            style={{
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "135%",
              color: "#323232",
              maxHeight: "132px",
            }}
          />
          <button
            onClick={sendMessage}
            disabled={loading || !chatSessionId}
            className="flex items-center justify-center transition-opacity duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            <SendIcon style={{ width: "27.4375px", height: "27.4375px" }} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default AIConversation;
